import { useCallback, useEffect, useRef, useState } from 'react';
import type { SandboxError } from '@arcanum-hq/shared-types';
import { ApiError } from '@/lib/api';
import { sandboxRecompute } from '@/lib/shares-api';

/**
 * `external-sharing` Phase 5 Session 5.4 (2026-05-09); extended by
 * `deal-signal-overhaul` Phase 5 Session 1 (2026-05-12) with sessionStorage
 * persistence + a non-hook static getter. Canonical import name is now
 * `useViewerOverrides` (re-exported from `./use-viewer-overrides`).
 *
 * Client-side override-state hook for the public deal-link sandbox flow.
 * The recipient's edits accumulate in a local `overrides` map (keyed by
 * namedRange — the same identifier `<InputCellEditor>` ships through
 * `onSubmit`); each edit POSTs the full override map to
 * `POST /api/shared/:shareId/sandbox-recompute` after a 250ms debounce.
 * The server returns recomputed cell values (keyed by `${sheet}!${A1}`)
 * which the consumer overlays on top of `useSharedModelView`'s
 * `cellValues` to drive the rendered grid + dependent panels.
 *
 * **Persistence**: `overrides` is sessionStorage-backed under the key
 * `arcanum:viewer-overrides:<shareId>`. Refresh-persists per-tab. Closing
 * the tab clears (sessionStorage semantics). The "fork to persist" path
 * (recipient flow) or "sign up to save" (anonymous flow) is the durable
 * landing strategy — sessionStorage is the bridge.
 *
 * **Non-hook static getter** `getViewerOverrides(shareId)` reads the
 * same storage key without invoking React. Phase 5 Session 3's
 * `useSharedStreaming.sendMessage` consumes this to snapshot the override
 * map at chat-POST submit time.
 *
 * **Wire-shape note** (gap-analysis 2026-05-09): the server response
 * field is `values` (not `recomputedValues`). The hook unwraps
 * `response.values` into the local `recomputedValues` state — different
 * names by design (the local-state name reflects the consumer-side
 * "overlay on top of base cellValues" concept).
 */

const STORAGE_KEY_PREFIX = 'arcanum:viewer-overrides:';
const storageKey = (shareId: string): string => `${STORAGE_KEY_PREFIX}${shareId}`;

function readFromStorage(shareId: string): Record<string, unknown> {
  try {
    const raw = window.sessionStorage.getItem(storageKey(shareId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    return {};
  } catch {
    return {};
  }
}

/**
 * Non-hook static getter — same storage key as the hook. Phase 5 Session 3
 * (chat-POST submit path) calls this to snapshot the override map without
 * invoking a React hook at fire time.
 */
export function getViewerOverrides(shareId: string): Record<string, unknown> {
  return readFromStorage(shareId);
}

export interface UseSandboxOverridesResult {
  /**
   * The user's local edits, keyed by namedRange. Cleared by
   * `clearOverrides()` or page refresh. Consumers read this for the
   * `<SandboxBanner>` mount gate (`Object.keys(overrides).length > 0`).
   */
  overrides: Record<string, unknown>;
  /**
   * Server's recomputed cell values from the most recent successful
   * sandbox POST. Keyed by `${sheet}!${A1}`. Consumer-side merge
   * pattern: `cellValues = { ...baseCellValues, ...recomputedValues }`.
   */
  recomputedValues: Record<string, unknown>;
  /**
   * Domain errors from the most recent server response (CIRCULAR_REF,
   * DIV_ZERO, etc.). NOT the same as transient transport errors —
   * `transientError` carries those.
   */
  errors: SandboxError[];
  /**
   * True while a POST is in flight (after debounce settles, before
   * response). Surfaces as a small "Recomputing…" indicator near the
   * edited cell. NOT shown via a global banner.
   */
  isLoading: boolean;
  /**
   * Non-domain transport errors — primarily 429 rate-limit. Auto-clears
   * on next successful POST. Consumer renders inline as a friendly
   * "slow down" toast/chip.
   */
  transientError: string | null;
  /**
   * Apply an edit. Optimistically patches `overrides`; debounces 250ms;
   * fires POST with the full overrides map. On 200 unwraps
   * `response.values` into `recomputedValues`. On 4xx surfaces in
   * `errors`. On 429 surfaces in `transientError`.
   */
  applyEdit: (namedRange: string, value: unknown) => void;
  /**
   * Reset all sandbox state — `overrides`, `recomputedValues`, `errors`,
   * `transientError`. Cancels any pending debounced POST. Used by the
   * "Reset to original" CTA in `<SandboxBanner shareMode="external-authed">`.
   */
  clearOverrides: () => void;
}

const DEBOUNCE_MS = 250;

export function useSandboxOverrides(
  shareId: string,
): UseSandboxOverridesResult {
  // sessionStorage-backed initial state. Lazy initializer reads the storage
  // key on mount; refresh-persistence comes from the write-through effect
  // below. Malformed JSON / quota errors fail-open to an empty map.
  const [overrides, setOverrides] = useState<Record<string, unknown>>(() =>
    readFromStorage(shareId),
  );
  const [recomputedValues, setRecomputedValues] = useState<
    Record<string, unknown>
  >({});
  const [errors, setErrors] = useState<SandboxError[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [transientError, setTransientError] = useState<string | null>(null);

  // Write-through to sessionStorage on every overrides change. Re-runs on
  // shareId change (remount under a different share re-reads fresh via the
  // lazy initializer above; this effect handles the in-mount writes).
  // Empty maps DON'T write — leaves storage cleanly null when the user
  // hasn't edited yet, and prevents the clearOverrides path from racing
  // with this effect to write `'{}'` back after the explicit removeItem.
  // QuotaExceededError + Safari private-mode silently no-op — the in-memory
  // state is still correct; only persistence is lost.
  useEffect(() => {
    if (Object.keys(overrides).length === 0) return;
    try {
      window.sessionStorage.setItem(storageKey(shareId), JSON.stringify(overrides));
    } catch {
      // intentional no-op
    }
  }, [shareId, overrides]);

  // Debounce timer + cancellation token. The token guards against
  // late-arriving response races (user edits, debounce fires, server
  // is slow, user edits again before first response → second response
  // shouldn't overwrite a stale recompute). Each POST captures the
  // current token at fire time and bails on response if the token has
  // moved on.
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestTokenRef = useRef(0);
  // Holds the latest overrides map for the debounced fire — using a
  // ref avoids stale closures inside the timer callback when the user
  // submits multiple edits within the debounce window.
  const latestOverridesRef = useRef<Record<string, unknown>>({});

  // Cancel any in-flight debounce on unmount.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, []);

  const fireRecompute = useCallback(
    async (overridesMap: Record<string, unknown>): Promise<void> => {
      const token = ++requestTokenRef.current;
      setIsLoading(true);
      try {
        const response = await sandboxRecompute(shareId, {
          overrides: overridesMap,
        });
        // Stale response — newer edit fired after this one; drop.
        if (token !== requestTokenRef.current) return;
        setRecomputedValues(response.values);
        setErrors(response.errors);
        setTransientError(null);
      } catch (err) {
        if (token !== requestTokenRef.current) return;
        if (err instanceof ApiError && err.status === 429) {
          setTransientError(
            "Slow down — you're editing faster than we can keep up.",
          );
        } else if (err instanceof ApiError) {
          setTransientError(
            `Couldn't recompute (${err.status}). Try again in a moment.`,
          );
        } else {
          setTransientError("Couldn't recompute. Try again in a moment.");
        }
      } finally {
        if (token === requestTokenRef.current) {
          setIsLoading(false);
        }
      }
    },
    [shareId],
  );

  const applyEdit = useCallback(
    (namedRange: string, value: unknown): void => {
      // Optimistic update — the consumer reads `overrides` to mount the
      // banner; this is the immediate signal that "user is editing".
      setOverrides((prev) => {
        const next = { ...prev, [namedRange]: value };
        latestOverridesRef.current = next;
        return next;
      });

      // Debounce: replace any pending timer with a fresh 250ms window.
      // Fast-typing collapses to one POST at the end.
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        void fireRecompute(latestOverridesRef.current);
      }, DEBOUNCE_MS);
    },
    [fireRecompute],
  );

  const clearOverrides = useCallback((): void => {
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    // Bump the request token so any in-flight response is discarded.
    requestTokenRef.current++;
    latestOverridesRef.current = {};
    setOverrides({});
    setRecomputedValues({});
    setErrors([]);
    setTransientError(null);
    setIsLoading(false);
    // Also wipe sessionStorage so a refresh after Reset doesn't restore
    // the cleared map. The write-through effect would re-fire on the
    // empty-map state change anyway, but removing the key is cleaner
    // (no stale `{}` entry sitting in storage).
    try {
      window.sessionStorage.removeItem(storageKey(shareId));
    } catch {
      // intentional no-op
    }
  }, [shareId]);

  return {
    overrides,
    recomputedValues,
    errors,
    isLoading,
    transientError,
    applyEdit,
    clearOverrides,
  };
}
